import { API_RESULT, AUTH_METHOD } from "@/utils/enums";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { saveTsClicksApi, sendOtpApi, getTsPaymentMethodApi } from "./UserApi";
import { SaveTsClicksResponse, SendOtpResponse, GetTsPaymentMethodResponse, Website } from "@/utils/types";

interface CreditCard {
  id: string;
  number?: string;
  // Add other credit card properties as needed
}

interface PendingWebsite {
  website: Website;
  affiliateType: string;
}

export interface EligibilityErrorBodyLink {
  label: string;
  href: string;
}

export interface EligibilityErrorInlineLink {
  placeholder: string;
  label: string;
  href: string;
}

export interface EligibilityErrorModalContent {
  returnCode: number;
  title: string;
  body: string;
  bodyBeforeLink?: string;
  bodyLink?: EligibilityErrorBodyLink;
  bodyAfterLink?: string;
  inlineLinks?: EligibilityErrorInlineLink[];
  encouragementText?: string;
  supportText?: string;
  supportInlineLinks?: EligibilityErrorInlineLink[];
  showDisclaimer?: boolean;
}

export interface PermissionErrorModalContent {
  title: string;
  body: string;
  footer?: string;
  /** When set, UIModal shows a primary action (e.g. navigate to login). */
  primaryButtonLabel?: string;
  /** If set, primary navigates to /login with this returnTo instead of the current path. */
  loginReturnTo?: string;
}

interface UserState {
  creditCards: CreditCard[];
  otpExpiryTime: number | null; // Timestamp when OTP expires (in milliseconds)
  otpValidityMinutes: number; // OTP validity duration in minutes
  isAuthenticated: boolean; // User authentication status
  payCardActivationExpiryTime: number | null; // Timestamp when Pay card activation expires (in milliseconds)
  userName: string | null; // User's name for greeting
  personalId: string | null; // Stored after login for saveTsClicksApi when clicking rewarding sites
  remainingUsageAmount: number; // From eligibility, for saveTsClicksApi
  groupMailingConfirmation: boolean;
  // Add other user state properties as needed
  sendOtpStatus: API_RESULT;
  sendOtpResponse: SendOtpResponse | null;
  saveTsClicksStatus: API_RESULT;
  saveTsClicksResponse: SaveTsClicksResponse | null;
  /** From save-ts-click on /flights entry; passed as feClickId to save-ts-flight (not Skyscanner session id). */
  flightsScreenSaveTsClickId: string;
  /** Per-website feClickId from save-ts-click, used for extend-ts-click-timer. */
  clickIdByWebsite: Record<string, string>;
  pendingWebsite: PendingWebsite | null; // Website user clicked before login
  eligibilityErrorModal: EligibilityErrorModalContent | null; // Shown after closing login modal on eligibility error
  permissionErrorModal: PermissionErrorModalContent | null; // 401/403 – shown via UIModal
  transferDisclaimerShown: boolean; // Whether user has seen the full transfer disclaimer modal (first-time flow)
  affiliateOutboundPending: boolean; // True after user is sent to an external affiliate site; triggers MarketingFloatingCard on tab return
  authMethod: AUTH_METHOD | null;
  authPhone: string | null;
  authEmail: string | null;
  tsPaymentMethodLoading: boolean;
  tsPaymentMethod: string | null;
}

const initialState: UserState = {
  creditCards: [],
  otpExpiryTime: null,
  otpValidityMinutes: 5, // 5 minutes default
  isAuthenticated: false,
  payCardActivationExpiryTime: null,
  userName: null,
  personalId: null,
  remainingUsageAmount: 0,
  groupMailingConfirmation: false,
  sendOtpStatus: API_RESULT.IDLE,
  sendOtpResponse: null,
  saveTsClicksStatus: API_RESULT.IDLE,
  saveTsClicksResponse: null,
  flightsScreenSaveTsClickId: "",
  clickIdByWebsite: {},
  pendingWebsite: null,
  eligibilityErrorModal: null,
  permissionErrorModal: null,
  transferDisclaimerShown: false,
  affiliateOutboundPending: false,
  authMethod: null,
  authPhone: null,
  authEmail: null,
  tsPaymentMethodLoading: false,
  tsPaymentMethod: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setCreditCards: (state, action: PayloadAction<CreditCard[]>) => {
      state.creditCards = action.payload;
    },
    addCreditCard: (state, action: PayloadAction<CreditCard>) => {
      state.creditCards.push(action.payload);
    },
    removeCreditCard: (state, action: PayloadAction<string>) => {
      state.creditCards = state.creditCards.filter(
        (card) => card.id !== action.payload
      );
    },
    startOtpTimer: (state) => {
      const now = Date.now();
      const expiryTime = now + state.otpValidityMinutes * 60 * 1000;
      state.otpExpiryTime = expiryTime;
    },
    clearOtpTimer: (state) => {
      state.otpExpiryTime = null;
    },
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
      if (action.payload) {
        // Start 2 hours timer when user is authenticated
        const now = Date.now();
        const twoHoursInMs = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
        state.payCardActivationExpiryTime = now + twoHoursInMs;
      } else {
        // Clear timer and auth data when user is not authenticated
        state.payCardActivationExpiryTime = null;
        state.personalId = null;
        state.remainingUsageAmount = 0;
        state.groupMailingConfirmation = false;
      }
    },
    setPayCardActivationExpiryTime: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.payCardActivationExpiryTime = action.payload;
    },
    setUserName: (state, action: PayloadAction<string | null>) => {
      state.userName = action.payload;
    },
    setAuthEligibilityData: (
      state,
      action: PayloadAction<{
        personalId: string;
        remainingUsageAmount: number;
        groupMailingConfirmation: boolean;
      }>
    ) => {
      state.personalId = action.payload.personalId;
      state.remainingUsageAmount = action.payload.remainingUsageAmount;
      state.groupMailingConfirmation = action.payload.groupMailingConfirmation;
    },
    updateSendOtpResponseSession: (state, action: PayloadAction<string>) => {
      if (state.sendOtpResponse) {
        state.sendOtpResponse.Session = action.payload;
      }
    },
    updateSaveTsClicksResponse: (state, action: PayloadAction<SaveTsClicksResponse>) => {
      state.saveTsClicksResponse = action.payload;
    },
    setFlightsScreenSaveTsClickId: (state, action: PayloadAction<string>) => {
      state.flightsScreenSaveTsClickId = action.payload;
    },
    setWebsiteClickId: (state, action: PayloadAction<{ websiteId: string; clickId: string }>) => {
      state.clickIdByWebsite[action.payload.websiteId] = action.payload.clickId;
    },
    setPendingWebsite: (state, action: PayloadAction<PendingWebsite | null>) => {
      state.pendingWebsite = action.payload;
    },
    clearPendingWebsite: (state) => {
      state.pendingWebsite = null;
    },
    setEligibilityErrorModal: (
      state,
      action: PayloadAction<EligibilityErrorModalContent | null>
    ) => {
      state.eligibilityErrorModal = action.payload;
    },
    clearEligibilityErrorModal: (state) => {
      state.eligibilityErrorModal = null;
    },
    setPermissionErrorModal: (
      state,
      action: PayloadAction<PermissionErrorModalContent | null>
    ) => {
      state.permissionErrorModal = action.payload;
    },
    clearPermissionErrorModal: (state) => {
      state.permissionErrorModal = null;
    },
    setTransferDisclaimerShown: (state, action: PayloadAction<boolean>) => {
      state.transferDisclaimerShown = action.payload;
    },
    setAffiliateOutboundPending: (state, action: PayloadAction<boolean>) => {
      state.affiliateOutboundPending = action.payload;
    },
    setAuthDetails: (
      state,
      action: PayloadAction<{ authMethod: AUTH_METHOD; authPhone?: string; authEmail?: string }>
    ) => {
      state.authMethod = action.payload.authMethod;
      state.authPhone = action.payload.authPhone ?? null;
      state.authEmail = action.payload.authEmail ?? null;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.userName = null;
      state.personalId = null;
      state.remainingUsageAmount = 0;
      state.groupMailingConfirmation = false;
      state.payCardActivationExpiryTime = null;
      state.transferDisclaimerShown = false;
      state.affiliateOutboundPending = false;
      state.otpExpiryTime = null;
      state.sendOtpResponse = null;
      state.pendingWebsite = null;
      state.eligibilityErrorModal = null;
      state.permissionErrorModal = null;
      state.authMethod = null;
      state.authPhone = null;
      state.authEmail = null;
      state.flightsScreenSaveTsClickId = "";
      state.clickIdByWebsite = {};
      state.tsPaymentMethodLoading = false;
      state.tsPaymentMethod = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sendOtpApi.pending, (state) => {
        state.sendOtpStatus = API_RESULT.LOADING;
      })
      .addCase(
        sendOtpApi.fulfilled,
        (state, action: PayloadAction<SendOtpResponse>) => {
          state.sendOtpStatus = API_RESULT.SUCCEEDED;
          state.sendOtpResponse = action.payload;
        }
      )
      .addCase(sendOtpApi.rejected, (state) => {
        state.sendOtpStatus = API_RESULT.FAILED;
        state.sendOtpResponse = null;
      })
      .addCase(saveTsClicksApi.pending, (state) => {
        state.saveTsClicksStatus = API_RESULT.LOADING;
      })
      .addCase(saveTsClicksApi.fulfilled, (state, action: PayloadAction<SaveTsClicksResponse>) => {
        state.saveTsClicksStatus = API_RESULT.SUCCEEDED;
        state.saveTsClicksResponse = action.payload;
      })
      .addCase(saveTsClicksApi.rejected, (state) => {
        state.saveTsClicksStatus = API_RESULT.FAILED;
        state.saveTsClicksResponse = null;
      })
      .addCase(getTsPaymentMethodApi.pending, (state) => {
        state.tsPaymentMethodLoading = true;
        state.tsPaymentMethod = null;
      })
      .addCase(getTsPaymentMethodApi.fulfilled, (state, action: PayloadAction<GetTsPaymentMethodResponse>) => {
        state.tsPaymentMethodLoading = false;
        state.tsPaymentMethod = action.payload.feCc4LastDigits ?? null;
      })
      .addCase(getTsPaymentMethodApi.rejected, (state) => {
        state.tsPaymentMethodLoading = false;
        state.tsPaymentMethod = null;
      });
  },
});

export const {
  setCreditCards,
  addCreditCard,
  removeCreditCard,
  startOtpTimer,
  clearOtpTimer,
  setIsAuthenticated,
  setPayCardActivationExpiryTime,
  setUserName,
  setAuthEligibilityData,
  updateSendOtpResponseSession,
  updateSaveTsClicksResponse,
  setFlightsScreenSaveTsClickId,
  setWebsiteClickId,
  setPendingWebsite,
  clearPendingWebsite,
  setEligibilityErrorModal,
  clearEligibilityErrorModal,
  setPermissionErrorModal,
  clearPermissionErrorModal,
  setTransferDisclaimerShown,
  setAffiliateOutboundPending,
  setAuthDetails,
  logout,
} = userSlice.actions;
export default userSlice.reducer;
